package com.example.crashbit

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.example.crashbit.data.*
import org.jetbrains.compose.resources.stringResource
import crashbit_multiplatform.composeapp.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun UniverseBrowserScreen(viewModel: SpaceViewModel) {
    val universes = viewModel.savedUniverses
    
    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(stringResource(Res.string.title_universe_browser), fontWeight = FontWeight.ExtraBold, letterSpacing = 2.sp) },
                navigationIcon = {
                    IconButton(onClick = { viewModel.backToTitle() }) {
                        Icon(AppIcons.ArrowBack, contentDescription = "Back")
                    }
                },
                colors = TopAppBarDefaults.topAppBarColors(
                    containerColor = Color.Black,
                    titleContentColor = Color.Yellow,
                    navigationIconContentColor = Color.White
                ),
                windowInsets = WindowInsets(0, 0, 0, 0)
            )
        },
        containerColor = Color.Black,
        contentWindowInsets = WindowInsets(0, 0, 0, 0)
    ) { padding ->
        if (universes.isEmpty()) {
            Box(modifier = Modifier.fillMaxSize().padding(padding), contentAlignment = Alignment.Center) {
                Text(stringResource(Res.string.label_no_saved_universes), color = Color.White.copy(alpha = 0.5f), fontWeight = FontWeight.Bold)
            }
        } else {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
                LazyColumn(
                    modifier = Modifier
                        .fillMaxHeight()
                        .widthIn(max = 800.dp)
                        .padding(padding),
                    contentPadding = PaddingValues(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    items(universes) { universe ->
                        UniverseCard(
                            universeWithContents = universe,
                            onLoad = { viewModel.loadUniverse(universe) },
                            onDelete = { viewModel.deleteGame(universe.universe) }
                        )
                    }
                }
            }
        }
    }
}

@OptIn(kotlin.time.ExperimentalTime::class)
@Composable
fun UniverseCard(
    universeWithContents: UniverseWithContentsModel,
    onLoad: () -> Unit,
    onDelete: () -> Unit
) {
    val u = universeWithContents.universe
    val bodies = universeWithContents.bodies
    
    val starCount = bodies.count { it.isStar && !it.isBlackHole }
    val blackHoleCount = bodies.count { it.isBlackHole }
    val planetCount = bodies.count { !it.isStar }
    val lifeCount = bodies.count { it.hasLife }
    val alienCount = bodies.count { it.hasAliens }
    
    val date = remember(u.createdAt) {
        val totalSec = u.createdAt / 1000
        val min = (totalSec / 60) % 60
        val hrs = (totalSec / 3600) % 24
        "Saved Game (${hrs.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')})"
    }

    Surface(
        color = Color.DarkGray.copy(alpha = 0.3f),
        shape = RoundedCornerShape(16.dp),
        modifier = Modifier.fillMaxWidth().clickable { onLoad() }
    ) {
        Column(modifier = Modifier.padding(16.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    text = u.name,
                    color = Color.White,
                    fontSize = 20.sp,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
                IconButton(onClick = onDelete) {
                    Icon(AppIcons.Delete, contentDescription = "Delete", tint = Color.Red.copy(alpha = 0.6f))
                }
            }
            
            Text(text = stringResource(Res.string.format_saved_date).replace("%s", date), color = Color.White.copy(alpha = 0.4f), fontSize = 10.sp)
            
            Spacer(modifier = Modifier.height(12.dp))
            
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                BrowserStat(AppIcons.Star, starCount.toString(), stringResource(Res.string.stat_stars), Color.Yellow)
                if (blackHoleCount > 0) {
                    BrowserStat(AppIcons.Star, blackHoleCount.toString(), stringResource(Res.string.stat_bh), Color(0xFF8B00FF))
                }
                BrowserStat(AppIcons.Public, planetCount.toString(), stringResource(Res.string.stat_planets), Color.Cyan)
                if (lifeCount > 0) {
                    BrowserStat(AppIcons.Eco, lifeCount.toString(), stringResource(Res.string.data_habitability).uppercase(), Color.Green)
                }
            }
            
            if (alienCount > 0) {
                Spacer(modifier = Modifier.height(8.dp))
                Surface(
                    color = Color.Magenta.copy(alpha = 0.1f),
                    shape = RoundedCornerShape(4.dp)
                ) {
                    Text(
                        text = stringResource(Res.string.label_technosignatures_detected),
                        color = Color.Magenta,
                        fontSize = 8.sp,
                        fontWeight = FontWeight.ExtraBold,
                        modifier = Modifier.padding(horizontal = 4.dp, vertical = 2.dp)
                    )
                }
            }
        }
    }
}

@Composable
fun BrowserStat(icon: androidx.compose.ui.graphics.vector.ImageVector, value: String, label: String, color: Color) {
    Row(verticalAlignment = Alignment.CenterVertically) {
        Icon(icon, contentDescription = null, tint = color.copy(alpha = 0.6f), modifier = Modifier.size(14.dp))
        Spacer(modifier = Modifier.width(4.dp))
        Column {
            Text(value, color = Color.White, fontSize = 12.sp, fontWeight = FontWeight.Bold)
            Text(label, color = Color.White.copy(alpha = 0.4f), fontSize = 8.sp)
        }
    }
}
